import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Formik, Form, useFormikContext, useField } from "formik";
import * as Yup from "yup";
import {
  Typography,
  Button,
  Box,
  TextField as MuiTextField,
  FormHelperText
} from "@mui/material";
import Modal from "../Modal/Modal";
import { TextField, CheckboxWithLabel } from "formik-mui";
import { Field } from "formik";
import { useTranslation } from "react-i18next";
import { validateHttpsUrl } from "../../utils/validation";
import { prependHttps } from "../../utils/string";

const LinkField = ({ showInvalidIframeMessage, onLinkChange }) => {
  const { t } = useTranslation();
  const { isSubmitting, submitCount } = useFormikContext();
  const [field, { error }, { setValue }] = useField("link");

  const showError = submitCount > 0 && !!error;

  const handleLinkChange = ({ target: { value } }) => {
    setValue(prependHttps(value));
    onLinkChange();
  };

  return (
    <>
      <MuiTextField
        hiddenLabel
        variant="filled"
        fullWidth
        placeholder="https://"
        inputProps={{ "data-qa": "add-link-modal-link-input" }}
        onChange={handleLinkChange}
        disabled={isSubmitting}
        value={field.value}
        error={showError}
        helperText={showError && error}
      />
      {showInvalidIframeMessage && (
        <FormHelperText variant="filled">
          {t("courseBuilder.invalidIframeHelperText")}
        </FormHelperText>
      )}
    </>
  );
};

export const AddLinkModalForm = ({
  onClose,
  open,
  preventsIframe,
  onLinkChange
}) => {
  const { t } = useTranslation();
  const { values, isSubmitting, resetForm, submitForm } = useFormikContext();

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <Form>
      <Modal
        scrollable
        disableEnforceFocus
        showCloseIcon
        maxWidth="sm"
        fullWidth
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {values.id
              ? t("courseBuilder.editLinkModalTitle")
              : t("courseBuilder.addLinkModalTitle")}
          </Typography>
        }
        dialogContent={
          <Box display="flex" flexDirection="column" mb={1}>
            <Box mb={3}>
              <Field
                variant="filled"
                fullWidth
                label={t("courseBuilder.addLinkTitleField")}
                component={TextField}
                name="title"
                inputProps={{ "data-qa": "add-link-modal-title-input" }}
              />
            </Box>
            <Box mb={3}>
              <LinkField
                showInvalidIframeMessage={preventsIframe}
                onLinkChange={onLinkChange}
              />
            </Box>
            <Box mb={3}>
              <Field
                component={CheckboxWithLabel}
                name="openInNewTab"
                Label={{ label: t("courseBuilder.addLinkOpenNewTabField") }}
                color="primary"
                type="checkbox"
              />
            </Box>
            <Box mb={3}>
              <Field
                variant="filled"
                fullWidth
                label={t("courseBuilder.addLinkDescriptionField")}
                component={TextField}
                multiline
                rows="4"
                name="description"
                inputProps={{ "data-qa": "add-link-modal-description-input" }}
              />
            </Box>
          </Box>
        }
        dialogActions={
          <>
            <Button color="primary" disabled={isSubmitting} onClick={onClose}>
              {t("courseBuilder.addLinkCancelBtn")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={submitForm}
              disabled={isSubmitting}
            >
              {values.id
                ? t("courseBuilder.editLinkSaveBtn")
                : t("courseBuilder.addLinkSaveBtn")}
            </Button>
          </>
        }
        open={open}
        onClose={onClose}
      />
    </Form>
  );
};
const getAddLinkSchema = t => {
  const maxTitleLength = 150;
  const maxDescriptionLength = 350;

  return Yup.object().shape({
    title: Yup.string()
      .required(t("validationMessages.titleRequired"))
      .max(
        maxTitleLength,
        t("validationMessages.cannotExceedLength", {
          type: "Title",
          maxLength: maxTitleLength
        })
      ),
    description: Yup.string().max(
      maxDescriptionLength,
      t("validationMessages.cannotExceedLength", {
        type: "Description",
        maxLength: maxDescriptionLength
      })
    ),
    link: Yup.mixed()
      .required(t("validationMessages.linkRequired"))
      .test("validUrl", t("validationMessages.validHttpsUrl"), function() {
        return validateHttpsUrl(this.parent.link);
      })
  });
};

const AddLinkModal = ({ onClose, open, onAddLink, itemEditing }) => {
  const { t } = useTranslation();
  const [preventsIframe, setPreventsIframe] = useState(false);

  useEffect(() => {
    if (itemEditing) {
      setPreventsIframe(!itemEditing.allowIframe);
    }
  }, [itemEditing]);

  let safeItem = itemEditing || {
    id: null,
    url: "",
    name: "",
    description: "",
    openInNewTab: false
  };
  if (itemEditing && itemEditing.contentableId) {
    safeItem.id = itemEditing.contentableId;
  }
  return (
    <Formik
      initialValues={{
        id: safeItem.id,
        link: safeItem.url,
        title: safeItem.name,
        description: safeItem.description,
        openInNewTab: safeItem.openInNewTab
      }}
      enableReinitialize
      validationSchema={getAddLinkSchema(t)}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        onAddLink(values);
      }}
    >
      {() => (
        <AddLinkModalForm
          open={open}
          onClose={onClose}
          itemEditing={itemEditing}
          onLinkChange={() => setPreventsIframe(false)}
          preventsIframe={preventsIframe}
        />
      )}
    </Formik>
  );
};

AddLinkModal.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to add a new link
   */
  onAddLink: PropTypes.func,
  /**
   * Populated when this is an existing item
   */
  itemEditing: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    url: PropTypes.string,
    description: PropTypes.string,
    openInNewTab: PropTypes.bool,
    contentableType: PropTypes.string,
    contentableId: PropTypes.string
  })
};

export default AddLinkModal;
